import {MinimalStorableSummaryWithSchemaId} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {isRequiredLinkCustomSchemaInterface} from 'schemas/fields/SchemaFieldConstants';
import APIUtil from 'util/APIUtil';

/** Concrete schema id -> the schema-interface (incl. fieldset) ids that schema implements. */
export type SchemaIdToImplementedInterfaces = Record<string, ReadonlySet<string>>;

const EMPTY_INTERFACE_SET: ReadonlySet<string> = new Set();

type SourceItem = BioentitySummary | MinimalStorableSummaryWithSchemaId;

/** The item's concrete schema id, or null when it has none. Storables never implement fieldsets. */
function getSourceItemSchemaId(item: SourceItem): string | null {
  if (APIUtil.isStorable(item.id)) {
    return null;
  }
  return (item as BioentitySummary).tagSchema?.id ?? null;
}

/**
 * The fieldset ids implemented by *every* selected item's schema (the intersection). A fieldset task
 * field only auto-maps when all selected items apply that fieldset, so we intersect, not union. Empty
 * when any item's schema is missing or implements nothing in common (strict: no partial matches).
 */
export function getImplementedInterfaceIdsForItems(
  items: ReadonlyArray<SourceItem>,
  interfacesBySchemaId: SchemaIdToImplementedInterfaces
): ReadonlySet<string> {
  let intersection: Set<string> | null = null;
  for (const item of items) {
    const schemaId = getSourceItemSchemaId(item);
    const implemented =
      schemaId != null ? (interfacesBySchemaId[schemaId] ?? EMPTY_INTERFACE_SET) : EMPTY_INTERFACE_SET;
    if (implemented.size === 0) {
      return EMPTY_INTERFACE_SET;
    }
    if (intersection == null) {
      intersection = new Set(implemented);
    } else {
      intersection = new Set([...intersection].filter((id) => implemented.has(id)));
    }
    if (intersection.size === 0) {
      return EMPTY_INTERFACE_SET;
    }
  }
  return intersection ?? EMPTY_INTERFACE_SET;
}

/** Whether any task field links to a fieldset. Gates the interfaces fetch to when it can matter. */
export function taskSchemaHasFieldsetField(taskSchema: {
  fields: ReadonlyArray<{requiredLink?: Parameters<typeof isRequiredLinkCustomSchemaInterface>[0]}>;
}): boolean {
  return taskSchema.fields.some((field) => isRequiredLinkCustomSchemaInterface(field.requiredLink));
}
